import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { ProductEvent, trackProductEvent } from '@common-services/product-analytics';
import {
    applySaveOfferDiscountThunk,
    cancelSubscriptionThunk,
    pauseSubscriptionThunk,
    recordCancelFlowDismissThunk,
    startAnnualSaveCheckoutThunk,
} from '@common-reducers/PaymentFormThunks';
import { useGetCancelFlowConfigQuery } from '@common-reducers/backend-api/backend-api';
import {
    cancelReasons,
    CancelReasonCode,
    CancelReasonError,
    DEFAULT_PAUSE_MONTHS,
    formatPrice,
    isSurveyComplete,
    PAUSE_OPTIONS_MONTHS,
    REASON_DETAIL_MAX_LENGTH,
    SAVE_OFFER_DISCOUNT_PCT,
    SaveOfferType,
    endsAccessNowOnCancel,
    offersForSubscription,
} from '@common-models/cancellation';
import { activeSubscriptionSelector } from '@common-reducers/UserSelectors';

const SUPPORT_EMAIL = 'support@blueticks.co';

export type CancelFlowResult = 'cancelled' | 'saved' | 'dismissed';

interface CancelFlowDialogProps {
    open: boolean;
    onClose: () => void;
    onFinished?: (result: CancelFlowResult) => void;
}

type Step = 'survey' | 'offer' | 'confirm';

/**
 * BE#498 — the fully-owned cancellation flow (replaces the Chargebee hosted-portal
 * cancel). Three steps: reason survey → ONE reason-matched save offer → confirm.
 *
 * Compliance (issue #498): the flow is no harder than signup; the cancel action is
 * ALWAYS visible on the offer step (the offer never blocks it); exactly ONE
 * retention offer is shown (the server's `maxOffers`, default 1; #241). Every path records a durable survey via the backend
 * (cancel path on the cancel action; save path inside the pause/discount action).
 *
 * Copy now goes through `t()` under the `cancelFlow.*` keys (the English is unchanged
 * from the first validation pass, so a copy sign-off changes a locale value and nothing
 * here). The reason labels come from `cancelReasons(t)` — they are the answer to
 * "why are you leaving?", which is worth asking in the customer's own language.
 */
export const CancelFlowDialog: React.FC<CancelFlowDialogProps> = ({ open, onClose, onFinished }) => {
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation(namespace);
    const [step, setStep] = useState<Step>('survey');
    const [reasonCode, setReasonCode] = useState<CancelReasonCode | ''>('');
    const [reasonDetail, setReasonDetail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // #241 — CANCEL_REASON_REQUIRED: "Something else" needs a written reason. The flag
    // comes from the server; a server refusal also turns it on, in case the config read
    // failed or the flag flipped while the dialog was open.
    const { data: flowConfig } = useGetCancelFlowConfigQuery(undefined, { skip: !open });
    const [serverRequiresReason, setServerRequiresReason] = useState(false);
    const [reasonError, setReasonError] = useState(false);
    const reasonRequired = !!flowConfig?.cancelReasonRequired || serverRequiresReason;
    const detailRequired = reasonRequired && reasonCode === 'other';
    const surveyComplete = isSurveyComplete(reasonCode, reasonDetail, reasonRequired);

    // #241 — a price objection with a server annual quote gets the yearly plan instead of
    // the discount; a second offer only shows if the server's maxOffers allows it.
    // #1182 — a trial gets the survey but no save offer, and cancelling ends access now.
    const activeSubscription = useAppSelector(activeSubscriptionSelector);
    const isTrial = activeSubscription?.status === 'in_trial';
    const endsNow = endsAccessNowOnCancel(activeSubscription);
    const offers = useMemo(() => offersForSubscription(reasonCode, flowConfig, isTrial), [reasonCode, flowConfig, isTrial]);
    const offer: SaveOfferType = offers[0] ?? 'none';
    const secondOffer: SaveOfferType | undefined = offers[1];
    const annualSave = flowConfig?.annualSave;

    // Analytics is best-effort — it must never break the flow. The server records cancels
    // and saves durably (cancellation_surveys); these client events add the OPENED and
    // DISMISSED legs the server can't see, completing the funnel in product analytics.
    const track = (event: ProductEvent, props: Record<string, any> = {}) => {
        try { trackProductEvent(event, { category: 'Subscription', ...props }); } catch { /* noop */ }
    };

    // Fire once each time the dialog opens.
    useEffect(() => {
        if (open) track(ProductEvent.CancelFlowOpened);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const reset = () => {
        setStep('survey');
        setReasonCode('');
        setReasonDetail('');
        setLoading(false);
        setError(null);
        setReasonError(false);
    };

    const closeWith = (result: CancelFlowResult) => {
        reset();
        onFinished?.(result);
        onClose();
    };

    const dismiss = () => {
        track(ProductEvent.CancelFlowDismissed, { step, reasonCode: reasonCode || undefined, offerShown: offer });
        // Durable "kept" record — a flow SUCCESS. Fire-and-forget; never block the close.
        dispatch(recordCancelFlowDismissThunk({
            reasonCode: reasonCode || undefined,
            reasonDetail: reasonDetail.trim() || undefined,
            offerShown: offer,
            step,
        }));
        closeWith('dismissed');
    };

    const proceedFromSurvey = () => {
        // Any offer other than 'none' gets one screen; otherwise straight to confirm.
        if (offer !== 'none') track(ProductEvent.CancelFlowOfferShown, { reasonCode, offer });
        setStep(offer === 'none' ? 'confirm' : 'offer');
    };

    const doCancel = async () => {
        setError(null);
        setLoading(true);
        try {
            await dispatch(
                cancelSubscriptionThunk({
                    cancellation: {
                        reasonCode: (reasonCode || 'other') as CancelReasonCode,
                        reasonDetail: reasonDetail.trim() || undefined,
                        offerShown: offer, // 'pause' | 'discount' | 'support' | 'annual' | 'none'
                        offerOutcome: offer === 'none' ? 'not_shown' : 'declined',
                    },
                }),
            ).unwrap();
            closeWith('cancelled');
        } catch (e) {
            const refused = e as Partial<CancelReasonError> | undefined;
            if (refused?.code === 'cancel_reason_required' || refused?.code === 'cancel_reason_detail_required') {
                // Back to the question, with the field marked, instead of a dead-end error.
                setServerRequiresReason(true);
                setReasonError(true);
                setLoading(false);
                setStep('survey');
                return;
            }
            setError(t('cancelFlow.error.cancel', 'We could not complete the cancellation. Please try again, or email {{email}}.', { email: SUPPORT_EMAIL }));
            setLoading(false);
        }
    };

    const doPause = async (resumeInMonths: number) => {
        setError(null);
        setLoading(true);
        try {
            const res: any = await dispatch(
                pauseSubscriptionThunk({ resumeInMonths }) as any,
            ).unwrap();
            if (res?.success) {
                track(ProductEvent.CancelFlowSaved, { offerType: 'pause', reasonCode, resumeInMonths });
                closeWith('saved');
            } else {
                setError(t('cancelFlow.error.pause', 'We could not pause your subscription. You can still cancel below.'));
                setLoading(false);
            }
        } catch {
            setError(t('cancelFlow.error.pause', 'We could not pause your subscription. You can still cancel below.'));
            setLoading(false);
        }
    };

    const doDiscount = async () => {
        setError(null);
        setLoading(true);
        try {
            const res: any = await dispatch(
                applySaveOfferDiscountThunk({
                    discountPct: SAVE_OFFER_DISCOUNT_PCT,
                    reasonCode: reasonCode || 'other',
                    reasonDetail: reasonDetail.trim() || undefined,
                }) as any,
            ).unwrap();
            if (res?.success) {
                track(ProductEvent.CancelFlowSaved, { offerType: 'discount', reasonCode, discountPct: SAVE_OFFER_DISCOUNT_PCT });
                closeWith('saved');
            } else {
                setError(t('cancelFlow.error.discount', 'We could not apply the discount. You can still cancel below.'));
                setLoading(false);
            }
        } catch {
            setError(t('cancelFlow.error.discount', 'We could not apply the discount. You can still cancel below.'));
            setLoading(false);
        }
    };

    const doAnnual = async () => {
        setError(null);
        setLoading(true);
        // Coming back from the hosted checkout (browser Back) restores this page frozen as it
        // was, spinner included; un-stick it.
        window.addEventListener('pageshow', (event: PageTransitionEvent) => {
            if (event.persisted) setLoading(false);
        }, { once: true });
        try {
            await dispatch(
                startAnnualSaveCheckoutThunk({
                    reasonCode: reasonCode || 'other',
                    reasonDetail: reasonDetail.trim() || undefined,
                }) as any,
            ).unwrap();
            // The browser is now leaving for the checkout; keep the spinner.
        } catch {
            setError(t('cancelFlow.annual.error', 'We could not open the yearly checkout. You can still cancel below.'));
            setLoading(false);
        }
    };

    const renderSurvey = () => (
        <>
            <DialogTitle>{t('cancelFlow.survey.title', "Before you go — what's driving this?")}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                    {t('cancelFlow.survey.body', 'Your answer helps us fix the right thing. Cancelling is always one click away.')}
                </Typography>
                {reasonError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('cancelFlow.survey.reasonRequired', "Please tell us why you're cancelling before you continue.")}
                    </Alert>
                )}
                <RadioGroup
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value as CancelReasonCode)}
                >
                    {cancelReasons(t).map((r) => (
                        <FormControlLabel key={r.code} value={r.code} control={<Radio />} label={r.label} />
                    ))}
                </RadioGroup>
                <TextField
                    label={detailRequired
                        ? t('cancelFlow.survey.detailLabelRequired', 'Please tell us what made you decide (required)')
                        : t('cancelFlow.survey.detailLabel', "Anything more you'd like us to know? (optional)")}
                    value={reasonDetail}
                    onChange={(e) => setReasonDetail(e.target.value)}
                    required={detailRequired}
                    error={reasonError && detailRequired && !reasonDetail.trim()}
                    inputProps={{ maxLength: REASON_DETAIL_MAX_LENGTH }}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={dismiss} color="inherit">{t('cancelFlow.keepSubscription', 'Keep my subscription')}</Button>
                <Button onClick={proceedFromSurvey} variant="contained" disabled={!surveyComplete}>
                    {t('cancelFlow.continue', 'Continue')}
                </Button>
            </DialogActions>
        </>
    );

    const renderOffer = () => {
        // Exactly one offer, matched to the reason. Cancel is always available.
        let title = '';
        let body: React.ReactNode = null;
        let primary: React.ReactNode = null;

        if (offer === 'annual' && annualSave) {
            const price = (cents: number) => formatPrice(cents, annualSave.currency, i18n.language);
            title = t('cancelFlow.annual.title', 'Pay yearly and save {{pct}}%', { pct: annualSave.pct });
            body = (
                <>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {t('cancelFlow.annual.body', 'Switch your plan to yearly billing and get {{pct}}% off the first year: {{offer}} instead of {{list}}. That works out to {{perMonth}} a month, compared with the {{current}} a month you pay now.', {
                            pct: annualSave.pct,
                            offer: price(annualSave.offerYearlyCents),
                            list: price(annualSave.listYearlyCents),
                            perMonth: price(Math.round(annualSave.offerYearlyCents / 12)),
                            current: price(annualSave.currentMonthlyCents),
                        })}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                        {t('cancelFlow.annual.note', "You'll review and pay on the next page. Nothing changes until you do.")}
                    </Typography>
                </>
            );
            primary = (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" disabled={loading} onClick={doAnnual}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : t('cancelFlow.annual.cta', 'Switch to yearly for {{offer}}', { offer: price(annualSave.offerYearlyCents) })}
                    </Button>
                    {secondOffer === 'discount' && (
                        <Button variant="outlined" disabled={loading} onClick={doDiscount}>
                            {t('cancelFlow.discount.cta', 'Apply {{pct}}% off', { pct: SAVE_OFFER_DISCOUNT_PCT })}
                        </Button>
                    )}
                </Box>
            );
        } else if (offer === 'pause') {
            title = t('cancelFlow.pause.title', 'Take a break instead of cancelling?');
            body = (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {t('cancelFlow.pause.body', "We'll pause your plan and keep everything exactly as it is — your data, settings and connections stay put. Billing stops while paused and picks back up when you return.")}
                </Typography>
            );
            primary = (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {PAUSE_OPTIONS_MONTHS.map((m) => (
                        <Button
                            key={m}
                            variant={m === DEFAULT_PAUSE_MONTHS ? 'contained' : 'outlined'}
                            disabled={loading}
                            onClick={() => doPause(m)}
                        >
                            {t('cancelFlow.pause.cta', { defaultValue_one: 'Pause {{count}} month', defaultValue_other: 'Pause {{count}} months', count: m })}
                        </Button>
                    ))}
                </Box>
            );
        } else if (offer === 'discount') {
            title = t('cancelFlow.discount.title', 'Stay for {{pct}}% off', { pct: SAVE_OFFER_DISCOUNT_PCT });
            body = (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {t('cancelFlow.discount.body', "We'd rather keep you than lose you over price. Here's {{pct}}% off your next renewal — applied instantly, nothing else changes.", { pct: SAVE_OFFER_DISCOUNT_PCT })}
                </Typography>
            );
            primary = (
                <Button variant="contained" disabled={loading} onClick={doDiscount}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : t('cancelFlow.discount.cta', 'Apply {{pct}}% off', { pct: SAVE_OFFER_DISCOUNT_PCT })}
                </Button>
            );
        } else {
            // support
            title = t('cancelFlow.support.title', 'Let us help before you go');
            body = (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {t('cancelFlow.support.body', "This sounds like something we can fix. Tell us what happened and we'll get on it — often it's a quick change on our side.")}
                </Typography>
            );
            primary = (
                <Button
                    variant="contained"
                    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('cancelFlow.support.mailSubject', 'Help before I cancel'))}`}
                    onClick={() => onFinished?.('dismissed')}
                >
                    {t('cancelFlow.support.cta', 'Contact support')}
                </Button>
            );
        }

        return (
            <>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    {body}
                    {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
                    <Box sx={{ mt: 3 }}>{primary}</Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={dismiss} color="inherit" disabled={loading}>{t('cancelFlow.keepSubscription', 'Keep my subscription')}</Button>
                    {/* Compliance: cancel is ALWAYS available on the offer step. */}
                    <Button onClick={() => setStep('confirm')} color="error" disabled={loading}>
                        {t('cancelFlow.declineOffer', 'No thanks, cancel')}
                    </Button>
                </DialogActions>
            </>
        );
    };

    const renderConfirm = () => (
        <>
            <DialogTitle>{endsNow ? t('cancelFlow.confirm.trialTitle', 'Cancel your trial?') : t('cancelFlow.confirm.title', 'Cancel your subscription?')}</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {endsNow
                        ? t('cancelFlow.confirm.trialWarning', 'Your trial will be cancelled now and your access ends immediately. You have not been charged.')
                        : t('cancelFlow.confirm.warning', "Your subscription will be cancelled at the end of the current term. You'll keep access until then.")}
                </Alert>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {t('cancelFlow.confirm.body', "We're sorry to see you go. If anything changes, you can resubscribe anytime.")}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={dismiss} color="inherit" disabled={loading}>{t('cancelFlow.keepSubscription', 'Keep my subscription')}</Button>
                <Button onClick={doCancel} variant="contained" color="error" disabled={loading} sx={{ minWidth: 160 }}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : t('cancelFlow.confirm.cta', 'Cancel subscription')}
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={open} onClose={loading ? undefined : dismiss} maxWidth="sm" fullWidth>
            {step === 'survey' && renderSurvey()}
            {step === 'offer' && renderOffer()}
            {step === 'confirm' && renderConfirm()}
        </Dialog>
    );
};

export default CancelFlowDialog;
